// Shared colour palette and tiny colour helpers (hex "#rrggbb" in, hex out; no THREE dependency).
// Values come from docs/DESIGN.md "Art direction".

/** Architecture neutrals. */
export const ARCH = {
  offWhite: "#e8e6e1",
  concrete: "#c9c7c2",
  midGrey: "#8d8c88",
  darkTrim: "#2a2b2e",
} as const;

/** Accent colours. Runner red marks routes. */
export const ACCENT = {
  runnerRed: "#e0362c",
  signalOrange: "#ff7a1a",
  teal: "#1fb5c9",
  safetyYellow: "#f2c230",
  deepBlue: "#2457d6",
} as const;

// Team colours. Orange and blue are a colour-blind safe pair and also differ in luminance (orange is
// about twice as bright), so they stay apart in greyscale. The FFA enemy is a magenta-crimson, far from
// both the team orange and the friendly teal.
export const TEAM_A_COLOR = "#ff8a1a";
export const TEAM_B_COLOR = "#2878ff";
export const ENEMY_COLOR = "#e5217f";
/** Team "none" without an enemy flag and without an explicit accent. */
export const NEUTRAL_COLOR = "#19c9b8";

export type TeamName = "a" | "b" | "none";

/** Primary colour of a player model: team a orange, team b cyan, FFA enemies red, otherwise accent/neutral. */
export function teamColor(team: TeamName, enemy = false, accent?: string): string {
  if (team === "a") return TEAM_A_COLOR;
  if (team === "b") return TEAM_B_COLOR;
  if (enemy) return ENEMY_COLOR;
  return accent ?? NEUTRAL_COLOR;
}

/** Gun materials. */
export const GUN = {
  polymer: "#34373c",
  polymerLight: "#4b4f56",
  steel: "#8d9299",
  steelDark: "#3f434a",
  steelBlack: "#23252a",
  accent: "#e0362c",
  sightDot: "#f1efe9",
  lens: "#7fd0e6",
  redDot: "#ff3030",
  laser: "#ff2222",
} as const;

/**
 * Character materials. The legs and boots are mid-tones (not near-black) so limbs read on dark
 * backdrops; gloves, glovesPad and strap are shared with the first-person hands.
 */
export const CHAR = {
  pants: "#78808a",
  pantsLight: "#b4bac3",
  boots: "#4b5058",
  sole: "#b9bcc2",
  gloves: "#26282c",
  glovesPad: "#4c4f55",
  shell: "#aeb4bd",
  shellShade: "#7d838d",
  visor: "#0c0e12",
  undersuit: "#5a606a",
  gear: "#68707a",
  strap: "#1d1f23",
  plate: "#d4d8de",
  sleeve: "#9aa1ab",
  belt: "#30343b",
} as const;

export interface Rgb {
  r: number; // 0..255
  g: number;
  b: number;
}

/** Parses "#rgb" or "#rrggbb". Anything unparsable yields mid grey. */
export function parseHex(hex: string): Rgb {
  let h = hex.trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length !== 6) return { r: 128, g: 128, b: 128 };
  const n = Number.parseInt(h, 16);
  if (!Number.isFinite(n)) return { r: 128, g: 128, b: 128 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number): string => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Multiplies the channels: f < 1 darkens, f > 1 brightens (clamped). */
export function shade(hex: string, f: number): string {
  const c = parseHex(hex);
  return rgbToHex(c.r * f, c.g * f, c.b * f);
}

/** Linear mix in sRGB space, t = 0 -> a, t = 1 -> b. */
export function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return rgbToHex(ca.r + (cb.r - ca.r) * t, ca.g + (cb.g - ca.g) * t, ca.b + (cb.b - ca.b) * t);
}

/** Perceptual-ish luminance 0..1 of an sRGB hex colour. */
export function luminance(hex: string): number {
  const c = parseHex(hex);
  return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
}

/** CSS rgba() string for canvas drawing. */
export function rgba(hex: string, alpha: number): string {
  const c = parseHex(hex);
  return `rgba(${c.r},${c.g},${c.b},${alpha})`;
}
